import { Link } from "react-router-dom";

const services = [
  {
    id: "01",
    title: "Max-Funded IUL",
    sub: "The Private Reserve Engine",
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80",
    imgAlt: "Financial planning documents",
    bullets: [
      "Market-linked growth with 0% downside floor",
      "Tax-free accumulation under IRC §7702",
      "Tax-free policy loans for income or opportunity capital",
      "Accelerated Death Benefits as living benefits",
    ],
  },
  {
    id: "02",
    title: "Tax-Free Retirement",
    sub: "Engineer Your Own Tax Code",
    img: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80",
    imgAlt: "Retirement planning strategy",
    bullets: [
      "No Required Minimum Distributions",
      "No contribution limits unlike qualified plans",
      "Income that doesn't trigger IRMAA surcharges",
      "Compliant with existing IRS code",
    ],
  },
  {
    id: "03",
    title: "Living Benefits",
    sub: "Your Policy Works While You're Alive",
    img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80",
    imgAlt: "Living benefits strategy",
    bullets: [
      "Critical illness and chronic care access",
      "Policy loans for real estate and business",
      "Zero surrender penalties when structured correctly",
      "Full death benefit preserved for estate transfer",
    ],
  },
  {
    id: "04",
    title: "Wealth Audit",
    sub: "The 360° Assessment",
    img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
    imgAlt: "Wealth audit and analysis",
    bullets: [
      "Tax liability mapping and rate projection",
      "Sequence-of-Returns analysis",
      "Business owner compensation optimization",
      "Holistic estate transfer planning",
    ],
  },
];

export default function Services() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-32 px-6 overflow-hidden text-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/95 via-navy/92 to-navy" />
        </div>
        <div className="relative max-w-2xl mx-auto">
          <p className="text-gold text-xs tracking-widest uppercase mb-4">The Wealth Design</p>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-4">
            Strategies, Not Products
          </h1>
          <span className="gold-line mx-auto" />
          <p className="text-white/55 text-lg">
            Private financial architectures engineered for tax efficiency, risk elimination, and liquidity.
          </p>
        </div>
      </section>

      {/* 2×2 Service Image Cards */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((s) => (
            <div key={s.id} className="group rounded-2xl overflow-hidden border border-gray-200 hover:border-gold/40 transition-colors duration-300 bg-white">
              <div className="relative h-56 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.imgAlt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-gold/50 font-display text-4xl font-bold">{s.id}</span>
                </div>
              </div>
              <div className="p-8">
                <h2 className="font-display text-2xl font-bold text-navy mb-1">{s.title}</h2>
                <p className="text-gold text-sm tracking-wide mb-4">{s.sub}</p>
                <ul className="space-y-2 mb-6">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-gray-600">
                      <span className="text-gold mt-1 shrink-0">✦</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="btn-outline text-sm">Get Started</Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works — 3 steps */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-gold text-xs tracking-widest uppercase mb-3">How It Works</p>
            <h2 className="section-title">The IUL at a Glance</h2>
            <span className="gold-line mx-auto" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-12">
            {[
              { step: "1", title: "Fund the Policy", body: "Contributions build cash value — engineered to maximize the asset, not the insurance." },
              { step: "2", title: "Index to the Market", body: "Cash value tracks a market index with a cap on gains and a 0% floor. You never go backward." },
              { step: "3", title: "Access Tax-Free", body: "Policy loans in retirement are not taxable. Draw income the IRS cannot touch." },
            ].map((s) => (
              <div key={s.step} className="card-dark text-center">
                <div className="w-10 h-10 rounded-full bg-gold text-navy text-sm font-bold flex items-center justify-center mx-auto mb-4">
                  {s.step}
                </div>
                <h3 className="font-display text-lg font-semibold text-navy mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/contact" className="btn-gold">Schedule Your Architecture Session</Link>
          </div>
        </div>
      </section>

      {/* Full-width CTA image */}
      <section className="relative h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1560472355-536de3962603?w=1920&q=80"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-navy-dark/80 flex items-center justify-center text-center px-6">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Your Wealth Has <span className="text-gold">Structural Gaps.</span>
            </h2>
            <Link to="/contact" className="btn-gold">Book a Free Strategy Audit</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
